import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Button, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { tokens } from '../theme';
import { useAuth, hasTier } from '../context/AuthContext';

interface TeaserProps {
  needs?: 'free' | 'pro';
  title?: string;
  features?: string[];
}

const FREE_FEATURES = [
  'Investment scores and cash-on-cash by bedroom',
  'Zillow home prices and market rent, Redfin sale-market stats',
  '20+ market reports, rankings and the heatmap',
  'Cash-flow, purchase-price and break-even calculators',
  'Historical FMR trends and the 110% payment-standard view',
];
const PRO_FEATURES = [
  'Live for-sale deals scored against Section 8 rents',
  'Unlimited deal alerts by ZIP, county or state',
  'Data exports and a personal API / MCP key',
];

/** "Sign up free to see this" card, shown in place of gated content. */
export const SignupTeaser: React.FC<TeaserProps> = ({ needs = 'free', title, features }) => {
  const { user } = useAuth();
  const location = useLocation();
  const next = encodeURIComponent(location.pathname + location.search);
  const isPro = needs === 'pro';
  const list = features || (isPro ? PRO_FEATURES : FREE_FEATURES);
  const heading = title || (isPro ? 'This is a Pro feature' : 'Sign up free to see this');

  return (
    <Box sx={{ my: 4, p: { xs: 2.5, md: 3.5 }, bgcolor: tokens.wash, borderRadius: '6px' }}>
      <Typography variant="h2" component="h2" sx={{ mb: 0.5 }}>{heading}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {isPro
          ? 'fmr8 Pro is $29 a month or $290 a year.'
          : 'A free account unlocks the investor data on fmr8. No credit card needed.'}
      </Typography>
      <Box component="ul" sx={{ m: 0, mb: 2, pl: 2.5 }}>
        {list.map((f) => <li key={f}><Typography variant="body2">{f}</Typography></li>)}
      </Box>
      <Stack direction="row" spacing={2} alignItems="center">
        {isPro ? (
          <Button variant="contained" component={RouterLink} to={user ? '/pro' : `/login?next=${encodeURIComponent('/pro')}`}>
            Upgrade to Pro
          </Button>
        ) : (
          <Button variant="contained" component={RouterLink} to={`/login?next=${next}`}>
            Sign up free
          </Button>
        )}
        <Box component={RouterLink} to="/pricing" sx={{ fontSize: 14, fontWeight: 600 }}>Compare plans</Box>
      </Stack>
    </Box>
  );
};

/** Renders children for users with the tier, otherwise `fallback` (default: nothing). */
export const Gated: React.FC<{ needs?: 'free' | 'pro'; fallback?: React.ReactNode; children: React.ReactNode }> = ({
  needs = 'free', fallback = null, children,
}) => {
  const { loading, tier } = useAuth();
  if (loading) return null;
  return <>{hasTier(tier, needs) ? children : fallback}</>;
};

/** Route wrapper for whole pages that need an account (reports, calculators, heatmap). */
export const RequireTierPage: React.FC<{ needs?: 'free' | 'pro'; title: string; children: React.ReactNode }> = ({
  needs = 'free', title, children,
}) => {
  const { loading, tier } = useAuth();
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (hasTier(tier, needs)) return <>{children}</>;
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>{title}</Typography>
      <SignupTeaser needs={needs} />
    </Container>
  );
};
